import React, { useMemo, useState, useEffect } from 'react'
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom'

import Landing from './views/Landing'
import ItemDetail from './views/ItemDetail'
import ItemList from './views/ItemList'
import Widget from './views/Widget'
import TextOnly from './views/TextOnly'
import CustomerDashboard from './views/CustomerDashboard'
import LeasePage from './views/LeasePage'

import './App.css'
import { Page, WebSite } from './types'
import StorageForm from './views/forms/StorageForm'
// import ReservationForm from './views/forms/ReservationForm'
import CancellationForm from './views/forms/CancellationForm'
import PhonePaymentPage from './views/PhonePaymentPage'
import PhonePostCallPage from './views/PhonePostCallPage'
import CompleteOnboarding from './views/CompleteOnboarding'
import LoginModal from './components/LoginModal'
import SessionExpiredModal from './components/SessionExpiredModal'
import ProtectedRoute from './components/ProtectedRoute'
import { pb } from './api-utils'
import { LoginModalProvider, useLoginModal } from './contexts/LoginModalContext'
import { SessionExpiredModalProvider, useSessionExpiredModal } from './contexts/SessionExpiredModalContext'
import packageJson from '../package.json'

// eslint-disable-next-line react-refresh/only-export-components
export const separator = <hr style={{ background: "#232043", height: 1.5 }} />

const Layout = ({ website, children }: { website: WebSite, children: React.ReactNode }) => {
  const [burguerMenuActive, setBurguerMenuActive] = useState(false)
  // const [activeMenu, setActiveMenu] = useState(website.config.defaultRoute)
  const { isLoginModalOpen, setIsLoginModalOpen, redirectOnLoginSuccess } = useLoginModal()
  const { isSessionExpiredModalOpen, setIsSessionExpiredModalOpen } = useSessionExpiredModal()
  const [isAuthenticated, setIsAuthenticated] = useState(pb.authStore.isValid)

  const location = useLocation()
  const navigate = useNavigate()
  const currentPath = useMemo(() => location.pathname, [location.pathname])
  const currentPage = useMemo(() => website.pages[Object.keys(website.pages).find(k => currentPath.endsWith(k))! || 'home'], [currentPath, website.pages])

  const toggleBurguerMenu = (skipIfNotOpen = false) => {
    if (skipIfNotOpen && burguerMenuActive === false) {
      return
    }
    setBurguerMenuActive(!burguerMenuActive)
  }

  // Subscribe to auth changes
  useEffect(() => {
    const unsubscribe = pb.authStore.onChange(() => {
      setIsAuthenticated(pb.authStore.isValid)
    })
    return unsubscribe
  }, [])

  // Validate token on mount - check if stored token is actually still valid
  useEffect(() => {
    const validateToken = async () => {
      if (pb.authStore.isValid && pb.authStore.record?.id) {
        try {
          // Try to fetch the current user to verify token is still valid
          await pb.collection('customers').getOne(pb.authStore.record.id)
          console.log('Auth token is valid')
        } catch (error: any) {
          // If we get a 401/403, the token is expired or invalid
          if (error?.status === 401 || error?.status === 403) {
            console.log('Auth token is expired or invalid. Showing session expired modal.')
            pb.authStore.clear()
            setIsAuthenticated(false)
            setIsSessionExpiredModalOpen(true)
          }
        }
      }
    }

    validateToken()
  }, [])

  // const handleMenuNav = (name: string) => {
  //   toggleBurguerMenu(true)
  //   if (activeMenu !== name) {
  //     setActiveMenu(name)
  //   }
  // }

  return (
    <div className="App">
      <div className="navbar" aria-label="main-navigation"
        style={{
          background: currentPage?.menu.useBackgroundGradient
            ? `linear-gradient(${currentPage?.gradient?.direction}, ${currentPage?.gradient?.first}, ${currentPage.gradient?.second})`
            : currentPage.menu.backgroundColor
        }}
      >
          <div className="navbar-brand">
            {website.config.logo.brandLogoUri
              ? <img
                className="navbar-item logo"
                src={new URL(`./site/images/${website.config.logo.brandLogoUri}`, import.meta.url).href}
                alt={website.config.logo.name}
                onClick={() => window.location.href = '/'}
                style={{ cursor: 'pointer' }}
              />
              : <h1>{website.config.logo.name}</h1>
            }
            <div
              className={`navbar-burger inline ${burguerMenuActive ? 'is-active' : 'inactive'}`}
              style={{ color: 'yellow' }}
              aria-label="menu"
              aria-expanded="false"
              onClick={() => toggleBurguerMenu()}
            >
              <span aria-hidden="true" style={{ height: 2 }} />
              <span aria-hidden="true" style={{ height: 2 }} />
              <span aria-hidden="true" style={{ height: 2 }} />
            </div>
          </div>
          <div className={burguerMenuActive ? 'navbar-menu is-active' : 'navbar-menu'}>
            <div className="navbar-end">
              {/* Render SMS, then phone, then email */}
              <a
                key="reserve"
                className="navbar-item is-size-6"
                style={{ fontWeight: 600, color: currentPage?.menu.color || 'inherit' }}
                href="/reserve"
                data-title="Reserve now"
              >
                <span className="navbar-item-text-and-icon" style={{ display: "flex", flexWrap: "nowrap" }}>
                  <i
                    className="menu-icon"
                    aria-hidden="true"
                    style={{ background: "yellow", borderRadius: 4, padding: "0.25rem", width: "1.15rem", height: "1.15rem", display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "nowrap" }}>
                    <span className="forceBlack" style={{ fontStyle: 'normal', fontSize: "0.85rem", paddingLeft: 2 }}>P</span>
                  </i>
                  Available Spaces
                </span>
              </a>
              {/* <a
                key="sms"
                className="navbar-item is-size-6"
                style={{ fontWeight: 600, color: currentPage?.menu.color || 'inherit' }}
                href="sms:6146992495"
                data-title="Text us"
              >
                <span className="navbar-item-text-and-icon">
                  <i className="menu-icon fa fa-comments" aria-hidden="true"></i>
                  (614) 699-2495
                </span>
              </a> */}
              <a
                key="phone-call"
                className="navbar-item is-size-6"
                style={{ fontWeight: 600, color: currentPage?.menu.color || 'inherit' }}
                href="tel:6146992495"
                data-title="Call us"
              >
                <span className="navbar-item-text-and-icon">
                  <i className="menu-icon fa fa-phone" aria-hidden="true"></i>
                  (614) 699-2495
                </span>
              </a>
              <a
                key="email"
                className="navbar-item is-size-6"
                style={{ fontWeight: 600, color: currentPage?.menu.color || 'inherit' }}
                href="mailto:hello@gahannarvandboat.com"
                data-title="Email us"
              >
                <span className="navbar-item-text-and-icon">
                  <i className="menu-icon fa fa-envelope" aria-hidden="true"></i>
                  hello@gahannarvandboat.com
                </span>
              </a>
              {website.pages["customer"].isHidden === false && isAuthenticated ? (
                <a
                  key="account"
                  className="navbar-item is-size-6"
                  style={{ fontWeight: 600, color: currentPage?.menu.color || 'inherit' }}
                  href="/customer"
                  data-title="My Account"
                >
                  <span className="navbar-item-text-and-icon">
                    <i className="menu-icon fa fa-user" aria-hidden="true"></i>
                    My Account
                  </span>
                </a>
              ) : website.pages["customer"].isHidden === false && (
                <a
                  key="login"
                  className="navbar-item is-size-6"
                  style={{ fontWeight: 600, color: currentPage?.menu.color || 'inherit', cursor: 'pointer' }}
                  onClick={() => setIsLoginModalOpen(true)}
                  data-title="Login"
                >
                  <span className="navbar-item-text-and-icon">
                    <i className="menu-icon fa fa-user" aria-hidden="true"></i>
                    Login
                  </span>
                </a>
              )}
            </div>
          </div>
        </div>

        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={(user) => {
            console.log('User logged in:', user)
            // Plain navbar "Login" click -> My Account. Login triggered mid-flow
            // (reserving a space, protected-page prompt, completing onboarding)
            // sets redirectOnLoginSuccess: false so the customer continues what
            // they were doing instead of being bounced to My Account.
            if (redirectOnLoginSuccess) {
              navigate('/customer/')
            }
          }}
        />

        <SessionExpiredModal
          isOpen={isSessionExpiredModalOpen}
          onClose={() => setIsSessionExpiredModalOpen(false)}
          onLoginClick={() => {
            setIsSessionExpiredModalOpen(false)
            setIsLoginModalOpen(true)
          }}
        />

        {children}
        <div className="footer">
          <div className="content has-text-centered is-size-4">
            <div style={{ gap: 30, display: "flex", flexDirection: "row", justifyContent: "space-between", paddingLeft: 40, paddingRight: 40, flexWrap: 'wrap' }}>
              <div className="footer-column">
                <div className="column-title">Business Hours</div>
                {website.config.footer.businessHours.map(({ day, hours }: ({ day: string, hours: string }), index: number) => (
                  <div key={index} style={{ textAlign: "left" }} className="is-size-6">
                    <span style={{ color: "white" }}>{day} <span style={{ color: "yellow" }}>{hours}</span></span>
                  </div>
                ))}
              </div>
              <div className="footer-column">
                <div className="column-title">Address</div>
                <div className="is-size-6">
                  <a target="_" href="https://www.google.com/maps/place/Gahanna+RV+%26+Boat+Storage/@40.0208334,-82.9056599,16z/data=!3m1!4b1!4m6!3m5!1s0x883861fcb5f3df29:0xeb3a49af8bf7e4c5!8m2!3d40.0208293!4d-82.9030796!16s%2Fg%2F1tf6x_xf?entry=ttu">
                    <div style={{ color: "yellow", textDecoration: "underline" }}>{website.config.footer.address}</div>
                    <div style={{ color: "yellow", textDecoration: "underline" }}>{`${website.config.footer.city}, ${website.config.footer.state} ${website.config.footer.zip}`}</div>
                  </a>
                </div>
              </div>
              <div className="footer-column">
                <div className="column-title">Contact Us</div>
                <div className="is-size-6">
                  <div><a style={{ color: "yellow", textDecoration: "underline" }} href={`mailto:${website.config.footer.email}`}>{website.config.footer.email}</a></div>
                  <div><a style={{ color: "yellow", textDecoration: "underline" }} href={`tel:${website.config.footer.phone}`}>{website.config.footer.phone}</a></div>
                </div>
              </div>
              <div className="footer-column">
                <div style={{ textAlign: "left" }}>
                  <div className="column-title">Social</div>
                  {website.config.footer.social.map((soc: { platformName: string, link: string }) => (
                    <div key={soc.platformName} className="is-size-6">
                      <a style={{ color: "yellow", textDecoration: "underline" }} target="_" href={soc.link}>{soc.platformName}</a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="footer-signature">
            <span>Gahanna RV and Boat Storage v{packageJson.version}</span>
            <br/>
            <span>
              Made with <span style={{ color: 'hotpink', fontSize: '1rem'}}>&#10084;</span> by{' '}
              <a style={{ color: "orange", textDecoration: 'underline', fontFamily: 'inherit' }} href="https://valentronix.com" target="_blank">
                Valentronix
              </a>
              <br/>
              <span>{`© ${(new Date()).getFullYear()}`} Valentronix LLC</span>
            </span>
          </div>
        </div>
      </div>
  )
}

const App = ({ website }: { website: WebSite }) => {
  const getElementForRoute = (page: Page) => {
    switch (page.pageType) {
      case 'landing-social': {
        return <Landing page={page} />
      }
      case 'item-detail': {
        return <ItemDetail page={page} />
      }
      case 'item-list': {
        return <ItemList page={page} />
      }
      case 'text-only': {
        return <TextOnly page={page} />
      }
      case 'storage-form': {
        return <StorageForm page={page} />
      }
      case 'cancellation-form': {
        return <CancellationForm page={page} />
      }
      case 'widget': {
        return <Widget page={page} />
      }
      case 'customer-dashboard': {
        return (
          <ProtectedRoute>
            <CustomerDashboard page={page} />
          </ProtectedRoute>
        )
      }
      case 'lease-management': {
        return (
          <ProtectedRoute>
            <LeasePage page={page} />
          </ProtectedRoute>
        )
      }
    }
  }

  const routes = Object.keys(website.pages)
    .filter(key => !website.pages[key].isHidden)
    .map((key) => ({
      path: key === 'home' ? "/" : `/${key}/`,
      element: (
        <LoginModalProvider>
          <SessionExpiredModalProvider>
            <Layout website={website}>
              {getElementForRoute(website.pages[key])}
            </Layout>
          </SessionExpiredModalProvider>
        </LoginModalProvider>
      ),
    }))

  // Phone-initiated reservation pages (standalone, no nav)
  routes.push({
    path: "/phone-pay",
    element: (
      <LoginModalProvider>
        <SessionExpiredModalProvider>
          <Layout website={website}>
            <PhonePaymentPage />
          </Layout>
        </SessionExpiredModalProvider>
      </LoginModalProvider>
    ),
  })
  routes.push({
    path: "/phone-complete",
    element: (
      <LoginModalProvider>
        <SessionExpiredModalProvider>
          <Layout website={website}>
            <PhonePostCallPage />
          </Layout>
        </SessionExpiredModalProvider>
      </LoginModalProvider>
    ),
  })

  routes.push({
    path: "/complete",
    element: (
      <LoginModalProvider>
        <SessionExpiredModalProvider>
          <Layout website={website}>
            <CompleteOnboarding />
          </Layout>
        </SessionExpiredModalProvider>
      </LoginModalProvider>
    ),
  })

  routes.push({
    path: "*",
    element: <Navigate to="/" replace />,
  })

  const router = createBrowserRouter(routes)

  return <RouterProvider router={router} />
}

export default App